import { pgTable, text, boolean, timestamp, integer, index, uniqueIndex, jsonb, foreignKey } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { user } from './user';
import { file } from './files';
import { promoAnnouncementEventType } from './enums';

export const teacher = pgTable('Teacher', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  userId: integer(),
  photoUrl: text(),
  photoId: integer(),
  order: integer().default(0).notNull(),
  isActive: boolean().default(true).notNull(),
  socialLinks: jsonb(),
  createdAt: timestamp({ precision: 3, mode: 'string' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'string' }).notNull(),
}, (table) => [
  index('Teacher_isActive_idx').using('btree', table.isActive.asc().nullsLast()),
  index('Teacher_order_idx').using('btree', table.order.asc().nullsLast()),
  uniqueIndex('Teacher_userId_key').using('btree', table.userId.asc().nullsLast()),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'Teacher_userId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.photoId],
    foreignColumns: [file.id],
    name: 'Teacher_photoId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
]);

// Alias for backward compatibility
export const resident = teacher;

export const announcement = pgTable('Announcement', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  slug: text().notNull(),
  thumbnailUrl: text(),
  eventDate: timestamp({ precision: 3, mode: 'string' }),
  isPublished: boolean().default(false).notNull(),
  viewsCount: integer().default(0).notNull(),
  order: integer().default(0).notNull(),
  createdAt: timestamp({ precision: 3, mode: 'string' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'string' }).notNull(),
}, (table) => [
  index('Announcement_eventDate_idx').using('btree', table.eventDate.asc().nullsLast()),
  index('Announcement_isPublished_idx').using('btree', table.isPublished.asc().nullsLast()),
  index('Announcement_order_idx').using('btree', table.order.asc().nullsLast()),
  index('Announcement_slug_idx').using('btree', table.slug.asc().nullsLast()),
  uniqueIndex('Announcement_slug_key').using('btree', table.slug.asc().nullsLast()),
]);

export const banner = pgTable('Banner', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  layout: jsonb(),
  linkUrl: text(),
  order: integer().default(0).notNull(),
  startDate: timestamp({ precision: 3, mode: 'string' }),
  endDate: timestamp({ precision: 3, mode: 'string' }),
  isActive: boolean().default(true).notNull(),
  viewsCount: integer().default(0).notNull(),
  clicksCount: integer().default(0).notNull(),
  createdAt: timestamp({ precision: 3, mode: 'string' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'string' }).notNull(),
}, (table) => [
  index('Banner_endDate_idx').using('btree', table.endDate.asc().nullsLast()),
  index('Banner_isActive_idx').using('btree', table.isActive.asc().nullsLast()),
  index('Banner_order_idx').using('btree', table.order.asc().nullsLast()),
  index('Banner_startDate_idx').using('btree', table.startDate.asc().nullsLast()),
]);

export const bannerImage = pgTable('BannerImage', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  bannerId: integer().notNull(),
  imageId: integer().notNull(),
  aspectRatio: text().notNull(),
  languageCode: text(),
  type: text().default('background').notNull(),
  order: integer().default(0).notNull(),
  createdAt: timestamp({ precision: 3, mode: 'string' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'string' }).notNull(),
}, (table) => [
  index('BannerImage_bannerId_idx').using('btree', table.bannerId.asc().nullsLast()),
  index('BannerImage_imageId_idx').using('btree', table.imageId.asc().nullsLast()),
  index('BannerImage_type_idx').using('btree', table.type.asc().nullsLast()),
  index('BannerImage_languageCode_idx').using('btree', table.languageCode.asc().nullsLast()),
  foreignKey({
    columns: [table.bannerId],
    foreignColumns: [banner.id],
    name: 'BannerImage_bannerId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.imageId],
    foreignColumns: [file.id],
    name: 'BannerImage_imageId_fkey',
  }).onUpdate('cascade').onDelete('restrict'),
]);

export const bannerView = pgTable('BannerView', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  bannerId: integer().notNull(),
  userId: integer(),
  createdAt: timestamp({ precision: 3, mode: 'string' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('BannerView_bannerId_idx').using('btree', table.bannerId.asc().nullsLast()),
  index('BannerView_userId_idx').using('btree', table.userId.asc().nullsLast()),
  index('BannerView_createdAt_idx').using('btree', table.createdAt.asc().nullsLast()),
  foreignKey({
    columns: [table.bannerId],
    foreignColumns: [banner.id],
    name: 'BannerView_bannerId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'BannerView_userId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
]);

export const bannerClick = pgTable('BannerClick', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  bannerId: integer().notNull(),
  userId: integer(),
  createdAt: timestamp({ precision: 3, mode: 'string' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('BannerClick_bannerId_idx').using('btree', table.bannerId.asc().nullsLast()),
  index('BannerClick_userId_idx').using('btree', table.userId.asc().nullsLast()),
  index('BannerClick_createdAt_idx').using('btree', table.createdAt.asc().nullsLast()),
  foreignKey({
    columns: [table.bannerId],
    foreignColumns: [banner.id],
    name: 'BannerClick_bannerId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'BannerClick_userId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
]);

// ── Promo Announcements ─────────────────────────────────────────────
// Fullscreen-промо при входе на платформу. Один активный по приоритету (order).
// Таргетинг: теги (видимость) + язык (изображения) + audienceSegment (правила).
// Per-user закрытие хранится на бэке (PromoAnnouncementDismissal) — закрытый не появляется.
export const promoAnnouncement = pgTable('PromoAnnouncement', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  linkUrl: text(),
  // SegmentDefinition (jsonb). NULL = виден всем (после фильтра тегов/языка). Как PromoCode.audienceSegment.
  audienceSegment: jsonb(),
  order: integer().default(0).notNull(),
  startDate: timestamp({ precision: 3, mode: 'string' }),
  endDate: timestamp({ precision: 3, mode: 'string' }),
  isActive: boolean().default(true).notNull(),
  viewsCount: integer().default(0).notNull(),
  clicksCount: integer().default(0).notNull(),
  closesCount: integer().default(0).notNull(),
  createdAt: timestamp({ precision: 3, mode: 'string' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'string' }).notNull(),
}, (table) => [
  index('PromoAnnouncement_endDate_idx').using('btree', table.endDate.asc().nullsLast()),
  index('PromoAnnouncement_isActive_idx').using('btree', table.isActive.asc().nullsLast()),
  index('PromoAnnouncement_order_idx').using('btree', table.order.asc().nullsLast()),
  index('PromoAnnouncement_startDate_idx').using('btree', table.startDate.asc().nullsLast()),
]);

export const promoAnnouncementImage = pgTable('PromoAnnouncementImage', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  announcementId: integer().notNull(),
  imageId: integer().notNull(),
  aspectRatio: text().notNull(),
  languageCode: text(),
  type: text().default('background').notNull(),
  order: integer().default(0).notNull(),
  createdAt: timestamp({ precision: 3, mode: 'string' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'string' }).notNull(),
}, (table) => [
  index('PromoAnnouncementImage_announcementId_idx').using('btree', table.announcementId.asc().nullsLast()),
  index('PromoAnnouncementImage_imageId_idx').using('btree', table.imageId.asc().nullsLast()),
  index('PromoAnnouncementImage_type_idx').using('btree', table.type.asc().nullsLast()),
  index('PromoAnnouncementImage_languageCode_idx').using('btree', table.languageCode.asc().nullsLast()),
  foreignKey({
    columns: [table.announcementId],
    foreignColumns: [promoAnnouncement.id],
    name: 'PromoAnnouncementImage_announcementId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.imageId],
    foreignColumns: [file.id],
    name: 'PromoAnnouncementImage_imageId_fkey',
  }).onUpdate('cascade').onDelete('restrict'),
]);

// Единая таблица аналитики: показ (IMPRESSION) / переход (CLICK) / закрытие (CLOSE).
export const promoAnnouncementEvent = pgTable('PromoAnnouncementEvent', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  announcementId: integer().notNull(),
  userId: integer(),
  type: promoAnnouncementEventType().notNull(),
  createdAt: timestamp({ precision: 3, mode: 'string' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('PromoAnnouncementEvent_announcementId_idx').using('btree', table.announcementId.asc().nullsLast()),
  index('PromoAnnouncementEvent_userId_idx').using('btree', table.userId.asc().nullsLast()),
  index('PromoAnnouncementEvent_type_idx').using('btree', table.type.asc().nullsLast()),
  index('PromoAnnouncementEvent_createdAt_idx').using('btree', table.createdAt.asc().nullsLast()),
  foreignKey({
    columns: [table.announcementId],
    foreignColumns: [promoAnnouncement.id],
    name: 'PromoAnnouncementEvent_announcementId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'PromoAnnouncementEvent_userId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
]);

// Факт закрытия анонса юзером — служит фильтром показа (закрытый не появляется снова).
export const promoAnnouncementDismissal = pgTable('PromoAnnouncementDismissal', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  announcementId: integer().notNull(),
  userId: integer().notNull(),
  dismissedAt: timestamp({ precision: 3, mode: 'string' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('PromoAnnouncementDismissal_announcementId_idx').using('btree', table.announcementId.asc().nullsLast()),
  index('PromoAnnouncementDismissal_userId_idx').using('btree', table.userId.asc().nullsLast()),
  uniqueIndex('PromoAnnouncementDismissal_announcementId_userId_key')
    .using('btree', table.announcementId.asc().nullsLast(), table.userId.asc().nullsLast()),
  foreignKey({
    columns: [table.announcementId],
    foreignColumns: [promoAnnouncement.id],
    name: 'PromoAnnouncementDismissal_announcementId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'PromoAnnouncementDismissal_userId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

export const legalPage = pgTable('LegalPage', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  slug: text().notNull(),
  isPublished: boolean().default(false).notNull(),
  createdAt: timestamp({ precision: 3, mode: 'string' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'string' }).notNull(),
}, (table) => [
  index('LegalPage_slug_idx').using('btree', table.slug.asc().nullsLast()),
  uniqueIndex('LegalPage_slug_key').using('btree', table.slug.asc().nullsLast()),
  index('LegalPage_isPublished_idx').using('btree', table.isPublished.asc().nullsLast()),
]);

export const article = pgTable('Article', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  slug: text().notNull(),
  thumbnailUrl: text(),
  isPublished: boolean().default(false).notNull(),
  viewsCount: integer().default(0).notNull(),
  likesCount: integer().default(0).notNull(),
  commentsCount: integer().default(0).notNull(),
  publishedAt: timestamp({ precision: 3, mode: 'string' }),
  createdAt: timestamp({ precision: 3, mode: 'string' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'string' }).notNull(),
}, (table) => [
  index('Article_isPublished_idx').using('btree', table.isPublished.asc().nullsLast()),
  index('Article_publishedAt_idx').using('btree', table.publishedAt.asc().nullsLast()),
  index('Article_slug_idx').using('btree', table.slug.asc().nullsLast()),
  uniqueIndex('Article_slug_key').using('btree', table.slug.asc().nullsLast()),
]);
